import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'

import { color } from 'styles/colors'

export type ToastType = 'success' | 'error';

interface ToastProps {
  open: boolean
  type?: ToastType;
  title: string;
  message: string;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ type = 'success', title, message, onClose, open }) => {
  const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (open) {
      if (autoHideTimer.current) clearTimeout(autoHideTimer.current)
      autoHideTimer.current = setTimeout(() => {
        onClose()
      }, 6000)
    } else {
      const timeout = setTimeout(() => onClose(), 400)
      return () => clearTimeout(timeout)
    }

    return () => {
      if (autoHideTimer.current) clearTimeout(autoHideTimer.current)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <Styled.Container type={type} visible={open}>
      <Styled.Content>
        {title && <Styled.Title type={type}>{title}</Styled.Title>}
        <Styled.Message>{message}</Styled.Message>
      </Styled.Content>
      <Styled.CloseButton onClick={onClose} aria-label="toast-button"><CloseIcon fontSize="small" /></Styled.CloseButton>
    </Styled.Container>
  )
}

const Styled = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['type', 'visible'].includes(prop)
  })<{ type: ToastType, visible: boolean }>`
    display: flex;
    align-items: start;
    background-color: ${color('paneBackground')};
    border-radius: 0.75rem;
    box-shadow: 0 4px 6px ${color('shadowSubtle')};
    padding: 1rem;
    margin: 1rem 0;
    width: 320px;
    top: 5%;
    left: 50%;
    position: fixed;
    border-left: 10px solid ${({ type }) => (type === 'success' ? color('legendOdSubCool') : color('danger'))};
    z-index: 1300;
    transition: opacity 0.4s ease, transform 0.4s ease, visibility 0.4s;
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    transform: ${({ visible }) =>
    visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-20px)'};
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
    `,

  Content: styled.div`
      flex-grow: 1;
    `,

  Title: styled.div.withConfig({
    shouldForwardProp: (prop) => !['type'].includes(prop)
  })<{ type: ToastType }>`
  font-weight: bold;
  font-size: 1rem;
  color: ${({ type }) => (type === 'success' ? color('legendOdSubCool') : color('danger'))};
`,

  Message: styled.div`
      font-size: 0.9rem;
      color: ${color('primaryText')};
    `,

  CloseButton: styled.button`
      background: none;
      border: none;
      font-size: 1rem;
      color: ${color('primaryText')};
      cursor: pointer;
      position: absolute;
      top: 0.5rem;
      right: 0.75rem;
      cursor: pointer;
    `
}

export default Toast
